"use client";

import { useEffect, useState } from "react";
import { invoke } from "@/lib/h5";

const PREFERENCE_COOKIE = "zests_do_not_share";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const hasSavedPreference = (value: boolean) =>
  document.cookie.split(";").some((cookie) => cookie.trim() === `${PREFERENCE_COOKIE}=${value}`);
type NativeBridge = { callNative: (payload: string) => void; hasAction?: (action: string) => boolean };
type AppPlatform = "android" | "ios" | null;
const getNativeBridge = () =>
  (window as Window & { NBJS?: NativeBridge }).NBJS;
const getAppPlatform = (appPreference: string | null): AppPlatform => {
  const bridge = getNativeBridge();
  if (typeof bridge?.callNative !== "function") return null;
  if (typeof bridge.hasAction === "function") {
    return bridge.hasAction("ccpa") ? "android" : null;
  }
  return appPreference === "0" || appPreference === "1" ? "ios" : null;
};

const Dsmi = () => {
  const [doNotShare, setDoNotShare] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [appPlatform, setAppPlatform] = useState<AppPlatform>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    const appPreference = new URLSearchParams(window.location.search).get("do_not_sell");
    const platform = getAppPlatform(appPreference);
    setAppPlatform(platform);
    setDoNotShare(platform && (appPreference === "0" || appPreference === "1")
      ? appPreference === "1"
      : hasSavedPreference(true));
    setMounted(true);
  }, []);

  const handleChange = (checked: boolean) => {
    setSaveError(false);
    if (appPlatform === "ios") {
      try {
        // iOS exposes callNative but its CCPA handler does not send a callback.
        const bridge = getNativeBridge();
        if (!bridge) throw new Error("Native bridge unavailable");
        bridge.callNative(JSON.stringify({ action: "ccpa", do_not_sell: checked }));
        setDoNotShare(checked);
      } catch {
        setSaveError(true);
      }
      return;
    }

    if (appPlatform === "android") {
      setSaving(true);
      invoke("ccpa", { do_not_sell: checked }, (response) => {
        setSaving(false);
        const saved = response.success === 1;
        setSaveError(!saved);
        if (saved) setDoNotShare(checked);
      });
      return;
    }

    document.cookie = `${PREFERENCE_COOKIE}=${checked}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;

    const saved = hasSavedPreference(checked);
    setSaveError(!saved);
    if (saved) setDoNotShare(checked);
  };

  return (
    <div
      className="bg-gradient-to-br from-gray-50 via-white to-blue-50 min-h-screen text-gray-800"
    >

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div
          className="bg-white/90 shadow-xl p-8 md:p-10 border border-gray-100 animate-fade-in"
        >
          <h3 className="text-xl font-semibold mb-5 text-gray-900">Trust Center</h3>

          <p className="text-gray-700 mb-2">Last Updated: May 19, 2025</p>

          <p className="text-gray-700 mb-4 leading-relaxed">
            Depending on what state you live in, you may have the right to opt out
            of "sales" of your personal information, sharing of personal
            information for cross-contextual behavioral advertising ("sharing"),
            or "processing of personal data for targeted advertising" as defined
            in applicable privacy law. Please read below for more information
            about these activities and how to opt out.
          </p>

          <p className="text-gray-700 mb-4 leading-relaxed">
            We do not sell personal information for monetary consideration, but we
            may disclose your information to third-party partners (including
            through the use of tracking technologies such as cookies and pixels)
            for the purpose of tailoring and displaying relevant advertisements to
            you on our site and on other platforms, as well as analyzing our
            advertising efforts.
            <strong
            >This data we may disclose does not identify you by name or other
              directly identifiable means.</strong>
            Rather, it is about the basic information related to your browser,
            device identifiers, your general location, and the content and
            advertising you view on our site and mobile application. However, as
            further described in our
            <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>,
            these types of disclosures may constitute "sales," "sharing," or
            "processing of personal data for targeted advertising" under certain
            US state privacy laws.
          </p>

          <p className="text-gray-700 mb-4 leading-relaxed">
            <strong
            >If your state provides you with the right to opt out of "selling,"
              "sharing," or "processing of personal data for targeted advertising"
              and you would like to exercise these rights, please check the box
              titled "Do Not Share My Information" below.</strong>
            Please note that opting out does not mean that you will stop seeing
            all ads.
          </p>

          <label className="flex items-center gap-2 text-gray-700 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={mounted && doNotShare}
              onChange={(event) => handleChange(event.target.checked)}
              disabled={saving}
              className="w-5 h-5 shrink-0 cursor-pointer"
            />
            <strong>Do Not Share My Information</strong>
          </label>
          {saveError && (
            <p className="text-sm text-red-700" role="alert">
              Your choice could not be saved. {appPlatform ? "Please try again." : "Please enable cookies and try again."}
            </p>
          )}
        </div>
      </div>

    </div>

  );
};

export default Dsmi;
